import {
  FC,
  ImgHTMLAttributes,
  MouseEvent,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import styled from "@emotion/styled";
import { assetProxyModule, MemCommonMarkdown } from "@mem-labs/common-editor";
import { observer } from "mobx-react-lite";
import { Doc } from "@/smart-views/client/api";
import {
  MDSDialogSlotName,
  MDSIcon,
  MDSIconColor,
  MDSIconKind,
  MDSModalDialog,
  MDSRowItem,
  MDSRowItemAppearance,
  MDSRowItemSlotName,
  MDSText,
  MDSTextVariant,
} from "@/mds";
import { FloatingNotePreview, ItemPreviewHoveringState } from "@/components/item-preview";
import { buildProxiedAssetUrl } from "@/modules/asset-proxy";
import { openWindowUrlInNewTab } from "@/modules/window/navigation";
import { AppStoreContext } from "@/store/utils/context";
import { ChippyText } from "./chips";
import { CitationChip } from "../citations/CitationChip";
import { SourcesPanel } from "../citations/SourcesPanel";

type BlockContent = Doc<"blockVersions">["content"];
type NoteReference = Extract<BlockContent, { kind: "noteList" }>["notes"][number];
type TimelineItem = Extract<BlockContent, { kind: "timeline" }>["items"][number];
type DateTextLinkItem = Extract<BlockContent, { kind: "dateTextLink" }>["items"][number];
type CollectionReference = Extract<BlockContent, { kind: "collectionShortcut" }>["collection"];
type CardGridItem = Extract<BlockContent, { kind: "cardGrid" }>["cards"][number];
type ImageItem = Extract<BlockContent, { kind: "multiImage" }>["images"][number];
type ResourceReference = Extract<BlockContent, { kind: "resourceList" }>["resources"][number];
type CalendarEvent = Extract<BlockContent, { kind: "calendarEvents" }>["events"][number];
type SourceChipAlignment = "start" | "end";

interface UserActionProps {
  isReadOnly?: boolean;
  onRequestAgentAction?: (message: string) => void;
}

const handleMarkdownMemItemClick = () => {};

export const NoteListBlock: FC<{ notes: NoteReference[] }> = ({ notes }) => {
  if (notes.length === 0) {
    return <EmptyState>No notes yet.</EmptyState>;
  }

  return (
    <ReferenceList>
      {notes.map((note, index) => (
        <NoteReferenceRow
          key={`${note.noteId}:${index}`}
          note={note}
          isLast={index === notes.length - 1}
        />
      ))}
    </ReferenceList>
  );
};

export const NoteShortcutBlock: FC<{ note: NoteReference }> = ({ note }) => (
  <ReferenceList>
    <NoteReferenceRow note={note} isLast />
  </ReferenceList>
);

export const ExpandedNoteBlock: FC<{ note: NoteReference }> = ({ note }) => (
  <ArticleBlock>
    <ItemTitle>
      <ChippyText text={note.title} />
    </ItemTitle>
    {note.date && <MetaText>{formatDateLabel(note.date)}</MetaText>}
    {note.body || note.snippet ? (
      <ArticleBody>
        {note.body ? (
          <ArticleMarkdown onClickMemItem={handleMarkdownMemItemClick} contentAlignment="start">
            {note.body}
          </ArticleMarkdown>
        ) : (
          <ChippyText text={note.snippet ?? ""} />
        )}
      </ArticleBody>
    ) : (
      <EmptyState>No note body yet.</EmptyState>
    )}
  </ArticleBlock>
);

export const TimelineBlock: FC<{ items: TimelineItem[] }> = ({ items }) => {
  if (items.length === 0) {
    return <EmptyState>No timeline items yet.</EmptyState>;
  }

  return (
    <TimelineList>
      {items.map((item, index) => (
        <TimelineRow key={item.id ?? `${item.title}:${index}`}>
          <TimelineMarker />
          <TimelineContent href={item.href}>
            <TimelineMain>
              {item.date && <MetaText>{formatDateLabel(item.date)}</MetaText>}
              <ItemTitle>
                <ChippyText text={item.title} />
              </ItemTitle>
              {item.body && (
                <BodyText>
                  <ChippyText text={item.body} />
                </BodyText>
              )}
            </TimelineMain>
            <SourceChips noteIds={item.sources ?? []} align="end" />
          </TimelineContent>
        </TimelineRow>
      ))}
    </TimelineList>
  );
};

export const DateTextLinkBlock: FC<{ items: DateTextLinkItem[] }> = ({ items }) => {
  if (items.length === 0) {
    return <EmptyState>No dated links yet.</EmptyState>;
  }

  return (
    <ReferenceList>
      {items.map((item, index) => (
        <ReferenceRow
          key={item.id ?? `${item.date}:${index}`}
          title={item.text}
          secondary={item.label}
          href={item.href}
          leftAddon={<DatePill>{formatShortDateLabel(item.date)}</DatePill>}
          rightAddon={item.href ? <MDSIcon kind={MDSIconKind.ArrowUpRight} /> : undefined}
          isLast={index === items.length - 1}
        />
      ))}
    </ReferenceList>
  );
};

export const CollectionShortcutBlock: FC<{ collection: CollectionReference }> = ({
  collection,
}) => (
  <CollectionShortcutSurface href={collection.href}>
    <ShortcutIcon>
      <MDSIcon kind={MDSIconKind.Collection} />
    </ShortcutIcon>
    <ItemContent>
      <ItemTitle>
        <ChippyText text={collection.title} />
      </ItemTitle>
      {collection.description && (
        <BodyText>
          <ChippyText text={collection.description} />
        </BodyText>
      )}
      <SourceChips noteIds={collection.sources ?? []} />
    </ItemContent>
  </CollectionShortcutSurface>
);

export const CardGridBlock: FC<{ cards: CardGridItem[] }> = ({ cards }) => {
  if (cards.length === 0) {
    return <EmptyState>No cards yet.</EmptyState>;
  }

  return (
    <CardGrid>
      {cards.map((card, index) => (
        <GridCard key={card.id ?? `${card.title}:${index}`} href={card.href}>
          {card.imageUrl ? (
            <CardImage src={card.imageUrl} alt={card.title} />
          ) : (
            <CardImageFallback>
              <MDSIcon kind={MDSIconKind.User} />
            </CardImageFallback>
          )}
          <GridCardBody>
            <ItemTitle>
              <ChippyText text={card.title} />
            </ItemTitle>
            {card.subtitle && <MetaText>{card.subtitle}</MetaText>}
            {card.body && (
              <BodyText>
                <ChippyText text={card.body} />
              </BodyText>
            )}
            <CardSourceNotes noteIds={card.sources ?? []} title={card.title} />
          </GridCardBody>
        </GridCard>
      ))}
    </CardGrid>
  );
};

export const SingleImageBlock: FC<{ image: ImageItem }> = ({ image }) => (
  <ImageFigure href={image.href}>
    {image.url ? (
      <HeroImage src={image.url} alt={image.alt ?? image.caption ?? ""} />
    ) : (
      <ImagePlaceholder>
        <MDSIcon kind={MDSIconKind.Image} />
      </ImagePlaceholder>
    )}
    {image.caption && (
      <Caption>
        <ChippyText text={image.caption} />
      </Caption>
    )}
    <SourceChips noteIds={image.sources ?? []} />
  </ImageFigure>
);

export const MultiImageBlock: FC<{ images: ImageItem[] }> = ({ images }) => {
  if (images.length === 0) {
    return <EmptyState>No images yet.</EmptyState>;
  }

  return (
    <ImageGrid>
      {images.map((image, index) => (
        <ImageTile key={`${image.url}:${index}`} href={image.href}>
          {image.url ? (
            <TileImage src={image.url} alt={image.alt ?? image.caption ?? ""} />
          ) : (
            <ImagePlaceholder>
              <MDSIcon kind={MDSIconKind.Image} />
            </ImagePlaceholder>
          )}
          {image.caption && (
            <Caption>
              <ChippyText text={image.caption} />
            </Caption>
          )}
        </ImageTile>
      ))}
    </ImageGrid>
  );
};

export const ResourceListBlock: FC<{ resources: ResourceReference[] }> = ({ resources }) => {
  if (resources.length === 0) {
    return <EmptyState>No resources yet.</EmptyState>;
  }

  return (
    <ReferenceList>
      {resources.map((resource, index) => (
        <ResourceReferenceRow
          key={resource.id ?? `${resource.title}:${index}`}
          resource={resource}
          isLast={index === resources.length - 1}
        />
      ))}
    </ReferenceList>
  );
};

export const ResourceShortcutBlock: FC<{ resource: ResourceReference }> = ({ resource }) => (
  <ReferenceList>
    <ResourceReferenceRow resource={resource} isLast />
  </ReferenceList>
);

export const CalendarEventsBlock: FC<
  {
    events: CalendarEvent[];
    newNoteLabel?: string;
  } & UserActionProps
> = ({ events, newNoteLabel = "New note", isReadOnly = false, onRequestAgentAction }) => (
  <CalendarEventList
    events={events}
    isReadOnly={isReadOnly}
    newNoteLabel={newNoteLabel}
    onRequestAgentAction={onRequestAgentAction}
  />
);

export const YearCalendarBlock: FC<{ year?: number; events: CalendarEvent[] }> = ({
  year,
  events,
}) => {
  const calendarYear = year ?? inferYear(events);

  return (
    <YearGrid>
      {Array.from({ length: 12 }).map((_, monthIndex) => {
        const monthEvents = eventsForMonth(events, calendarYear, monthIndex);
        const monthDate = new Date(calendarYear, monthIndex, 1);
        const monthDays = monthEvents.length > 0 ? buildMonthCells(monthDate) : [];

        return (
          <MiniMonth key={monthIndex}>
            <MiniMonthTitle>{MONTH_LABELS[monthIndex]}</MiniMonthTitle>
            {monthDays.length > 0 && (
              <MiniMonthDays>
                {monthDays.map((day, dayIndex) => {
                  const dayEvents = day ? eventsForDate(monthEvents, toDateKey(day)) : [];

                  return (
                    <MiniMonthDayCell key={day?.toISOString() ?? `blank:${dayIndex}`}>
                      {day && (
                        <MiniMonthDay $hasEvents={dayEvents.length > 0}>
                          {day.getDate()}
                        </MiniMonthDay>
                      )}
                      {dayEvents.length > 0 && (
                        <MiniDayPreview data-mini-day-preview>
                          {dayEvents.map(event => (
                            <MiniDayPreviewItem key={event.id ?? `${event.start}:${event.title}`}>
                              {formatCalendarEventLabel(event)}
                            </MiniDayPreviewItem>
                          ))}
                        </MiniDayPreview>
                      )}
                    </MiniMonthDayCell>
                  );
                })}
              </MiniMonthDays>
            )}
          </MiniMonth>
        );
      })}
    </YearGrid>
  );
};

export const MonthCalendarBlock: FC<{ month: string; events: CalendarEvent[] }> = ({
  month,
  events,
}) => {
  const monthDate = parseMonth(month, events);
  const days = buildMonthCells(monthDate);

  return (
    <MonthGrid>
      {WEEKDAY_LABELS.map(day => (
        <WeekdayHeader key={day}>{day}</WeekdayHeader>
      ))}
      {days.map((day, index) => {
        const dayEvents = day ? eventsForDate(events, toDateKey(day)) : [];

        return (
          <MonthDay key={day?.toISOString() ?? `blank:${index}`} $isMuted={!day}>
            {day && <DayNumber>{day.getDate()}</DayNumber>}
            {dayEvents.slice(0, 3).map(event => (
              <CalendarPill key={event.id ?? `${event.start}:${event.title}`}>
                <ChippyText text={formatCalendarEventLabel(event)} />
              </CalendarPill>
            ))}
          </MonthDay>
        );
      })}
    </MonthGrid>
  );
};

export const WeekCalendarBlock: FC<{ weekStart: string; events: CalendarEvent[] }> = ({
  weekStart,
  events,
}) => {
  const start = parseDate(weekStart) ?? startOfWeek(parseDate(events[0]?.start) ?? new Date());
  const days = Array.from({ length: 7 }).map((_, offset) => addDays(start, offset));

  return (
    <WeekGrid>
      {days.map(day => {
        const dayEvents = eventsForDate(events, toDateKey(day));

        return (
          <WeekDay key={toDateKey(day)}>
            <WeekDayTitle>
              <span>{WEEKDAY_LABELS[day.getDay()]}</span>
              <strong>{day.getDate()}</strong>
            </WeekDayTitle>
            {dayEvents.length === 0 ? (
              <EmptyMiniText>Free</EmptyMiniText>
            ) : (
              dayEvents.map(event => (
                <CalendarPill key={event.id ?? `${event.start}:${event.title}`}>
                  <ChippyText text={formatCalendarEventLabel(event)} />
                </CalendarPill>
              ))
            )}
          </WeekDay>
        );
      })}
    </WeekGrid>
  );
};

export const DayAgendaBlock: FC<{ date: string; events: CalendarEvent[] } & UserActionProps> = ({
  date,
  events,
  isReadOnly = false,
  onRequestAgentAction,
}) => {
  const targetDate = parseDate(date);
  const dayEvents = targetDate ? eventsForDate(events, toDateKey(targetDate)) : events;

  return (
    <AgendaStack>
      {targetDate && <MetaText>{formatDateLabel(toDateKey(targetDate))}</MetaText>}
      <CalendarEventList
        events={dayEvents}
        isReadOnly={isReadOnly}
        newNoteLabel="New note"
        onRequestAgentAction={onRequestAgentAction}
      />
    </AgendaStack>
  );
};

const CalendarEventList: FC<
  {
    events: CalendarEvent[];
    newNoteLabel: string;
  } & UserActionProps
> = ({ events, newNoteLabel, isReadOnly = false, onRequestAgentAction }) => {
  if (events.length === 0) {
    return <EmptyState>No events yet.</EmptyState>;
  }

  const normalizedNewNoteLabel = newNoteLabel.trim() || "New note";
  const isNewNoteDisabled = isReadOnly || !onRequestAgentAction;

  return (
    <ReferenceList>
      {events.map((event, index) => (
        <ReferenceRow
          key={event.id ?? `${event.start}:${index}`}
          title={event.title}
          secondary={event.location}
          href={event.href}
          noteId={event.noteId}
          leftAddon={<DatePill>{formatTimeRange(event)}</DatePill>}
          rightAddon={
            <EventActions>
              {event.href && <MDSIcon kind={MDSIconKind.ArrowUpRight} />}
              <NewNoteButton
                type="button"
                disabled={isNewNoteDisabled}
                aria-label={`${normalizedNewNoteLabel} for ${event.title}`}
                onClick={eventClick => {
                  eventClick.preventDefault();
                  eventClick.stopPropagation();
                  if (isNewNoteDisabled) return;
                  onRequestAgentAction?.(buildCalendarEventNoteRequest(event));
                }}
              >
                <MDSIcon kind={MDSIconKind.ComposeInline} />
                {normalizedNewNoteLabel}
              </NewNoteButton>
            </EventActions>
          }
          isLast={index === events.length - 1}
        />
      ))}
    </ReferenceList>
  );
};

const NoteReferenceRow: FC<{ note: NoteReference; isLast?: boolean }> = ({ note, isLast }) => (
  <ReferenceRow
    title={note.title}
    secondary={note.snippet}
    href={note.href}
    noteId={note.noteId}
    isLast={isLast}
  />
);

const ResourceReferenceRow: FC<{ resource: ResourceReference; isLast?: boolean }> = ({
  resource,
  isLast,
}) => (
  <ReferenceRow
    title={resource.title}
    secondary={resource.subtitle}
    href={resource.href ?? resource.url}
    noteId={resource.noteId}
    leftAddon={
      <ResourceIconChip>
        <MDSIcon
          kind={iconForResourceKind(resource.kind)}
          size={12}
          boxSize={16}
          color={MDSIconColor.Inherit}
        />
      </ResourceIconChip>
    }
    rightAddon={
      resource.href || resource.url ? <MDSIcon kind={MDSIconKind.ArrowUpRight} /> : undefined
    }
    isLast={isLast}
  />
);

const ReferenceRow: FC<{
  title: string;
  secondary?: string;
  href?: string;
  noteId?: string;
  leftAddon?: ReactNode;
  rightAddon?: ReactNode;
  isLast?: boolean;
}> = observer(function ReferenceRow({
  title,
  secondary,
  href,
  noteId,
  leftAddon,
  rightAddon,
  isLast = false,
}) {
  const appStore = useContext(AppStoreContext);
  const store = appStore?.store;
  const hoverState = useMemo(() => new ItemPreviewHoveringState(), []);
  const noteObservable = noteId ? store?.notes.getNoteObservableById({ noteId }) : undefined;
  const hasAction = Boolean(noteId || href);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (!hasAction) return;

    event.preventDefault();
    event.stopPropagation();
    hoverState.deactivateImmediately();

    if (noteId && store) {
      store.navigation.goToNote({ noteId });
      return;
    }

    if (href) {
      try {
        void openWindowUrlInNewTab({ url: new URL(href, document.baseURI) });
      } catch {
        // Ignore malformed generated links; the row still renders useful context.
      }
    }
  };

  const handleMouseEnter = (event: MouseEvent<HTMLElement>) => {
    if (!noteObservable) return;
    hoverState.setHoveredRect(event.currentTarget.getBoundingClientRect());
    hoverState.handleMouseEnter();
  };

  const handleMouseLeave = () => {
    hoverState.setHoveredRect(null);
    hoverState.handleMouseLeave();
  };

  return (
    <ReferenceRowWrapper>
      <MDSRowItem
        $appearance={MDSRowItemAppearance.Standard}
        $isHighlighted={undefined}
        $isHovered={undefined}
        $isSelected={undefined}
        $isSpecial={undefined}
        $mustClickOnRowTitle={!hasAction}
        className="mds-item-list-row"
        noSeparator={isLast}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {leftAddon && (
          <TopAlignedAddon slot={MDSRowItemSlotName.LeftAddon}>{leftAddon}</TopAlignedAddon>
        )}
        <ReferenceTitle>
          <ChippyText text={title} />
        </ReferenceTitle>
        {secondary && (
          <ReferenceSecondary slot={MDSRowItemSlotName.BottomAddon}>
            <ChippyText text={secondary} />
          </ReferenceSecondary>
        )}
        {rightAddon && (
          <ReferenceRightAddon slot={MDSRowItemSlotName.RightAddon}>
            {rightAddon}
          </ReferenceRightAddon>
        )}
      </MDSRowItem>
      {noteObservable && <FloatingNotePreview state={hoverState} noteObservable={noteObservable} />}
    </ReferenceRowWrapper>
  );
});

const ProxiedImage: FC<ImgHTMLAttributes<HTMLImageElement>> = observer(function ProxiedImage({
  src,
  ...props
}) {
  const appStore = useContext(AppStoreContext);
  const assetAccessTokenState = appStore?.store.auth.assetAccessTokenState;
  const originalUrl =
    typeof src === "string"
      ? (assetProxyModule.extractOriginalUrlFromProxy({ proxiedUrl: src }) ?? src)
      : undefined;

  useEffect(() => {
    if (!originalUrl || originalUrl.startsWith("blob:") || originalUrl.startsWith("data:")) {
      return;
    }

    if (assetAccessTokenState?.token) {
      return;
    }

    void assetAccessTokenState?.ensureToken();
  }, [assetAccessTokenState, originalUrl]);

  const proxiedSrc =
    buildProxiedAssetUrl({
      originalUrl,
      assetAccessTokenState,
    }) ??
    originalUrl ??
    src;

  return <img {...props} src={proxiedSrc} />;
});

const CardSourceNotes: FC<{ noteIds: Array<string | undefined>; title: string }> = ({
  noteIds,
  title,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const uniqueNoteIds = dedupeNoteIds(noteIds);

  if (uniqueNoteIds.length === 0) {
    return null;
  }

  return (
    <>
      <CardSourcesButton type="button" onClick={() => setIsOpen(true)}>
        <MDSIcon kind={MDSIconKind.Document} size={11} boxSize={16} color={MDSIconColor.Inherit} />
        <span>Sources</span>
        <CardSourcesCount>
          {uniqueNoteIds.length} note{uniqueNoteIds.length === 1 ? "" : "s"}
        </CardSourcesCount>
        <MDSIcon
          kind={MDSIconKind.AngleRight}
          size={11}
          boxSize={12}
          color={MDSIconColor.Inherit}
        />
      </CardSourcesButton>
      <SourcesNotesDialog isOpen={isOpen} handleClose={() => setIsOpen(false)}>
        <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
          Sources for {title}
        </MDSText>
        <SourcesDialogBody slot={MDSDialogSlotName.Content}>
          <SourcesPanel noteIds={uniqueNoteIds} isOpen={true} />
        </SourcesDialogBody>
      </SourcesNotesDialog>
    </>
  );
};

const SourceChips: FC<{
  noteIds: Array<string | undefined>;
  align?: SourceChipAlignment;
}> = ({ noteIds, align = "start" }) => {
  const uniqueNoteIds = dedupeNoteIds(noteIds);

  if (uniqueNoteIds.length === 0) {
    return null;
  }

  return (
    <InlineSources $align={align}>
      {uniqueNoteIds.map(noteId => (
        <CitationChip key={noteId} noteId={noteId} />
      ))}
    </InlineSources>
  );
};

const OptionalLink: FC<{
  href?: string;
  className?: string;
  children: ReactNode;
}> = ({ href, className, children }) => {
  if (!href) {
    return <div className={className}>{children}</div>;
  }

  return (
    <a className={className} href={href} target="_blank" rel="noreferrer">
      {children}
    </a>
  );
};

const dedupeNoteIds = (noteIds: Array<string | undefined>): string[] => {
  return Array.from(new Set(noteIds.filter((noteId): noteId is string => !!noteId)));
};

const buildCalendarEventNoteRequest = (event: CalendarEvent): string => {
  const parts = [
    `Create or update a note for the calendar event "${event.title}".`,
    `Event time: ${formatTimeRange(event)}.`,
  ];

  if (event.location) {
    parts.push(`Location: ${event.location}.`);
  }

  if (event.noteId) {
    parts.push(`Use related note ${event.noteId} if it is the right source note.`);
  }

  return parts.join(" ");
};

const iconForResourceKind = (kind: ResourceReference["kind"]): MDSIconKind => {
  switch (kind) {
    case "pdf":
      return MDSIconKind.Pdf;
    case "note":
      return MDSIconKind.Document;
    case "image":
      return MDSIconKind.Image;
    case "collection":
      return MDSIconKind.Collection;
    case "link":
      return MDSIconKind.Link;
    case "other":
      return MDSIconKind.Paperclip;
    default:
      return MDSIconKind.Paperclip;
  }
};

const formatDateLabel = (value: string): string => {
  const parsed = parseDate(value);
  if (!parsed) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(parsed);
};

const formatShortDateLabel = (value: string): string => {
  const parsed = parseDate(value);
  if (!parsed) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
  }).format(parsed);
};

const formatTimeRange = (event: CalendarEvent): string => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(event.start)) {
    return formatShortDateLabel(event.start);
  }

  const start = parseDate(event.start);
  const end = event.end ? parseDate(event.end) : null;

  if (!start) {
    return event.start;
  }

  const timeFormatter = new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });

  if (!end) {
    return timeFormatter.format(start);
  }

  return `${timeFormatter.format(start)}-${timeFormatter.format(end)}`;
};

const formatCalendarEventLabel = (event: CalendarEvent): string => {
  const timePrefix = formatEventTimePrefix(event);
  return timePrefix ? `${timePrefix} ${event.title}` : event.title;
};

const formatEventTimePrefix = (event: CalendarEvent): string => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(event.start)) {
    return "";
  }

  const start = parseDate(event.start);
  const end = event.end ? parseDate(event.end) : null;
  if (!start) {
    return "";
  }

  const timeFormatter = new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });

  return end
    ? `${timeFormatter.format(start)}-${timeFormatter.format(end)}`
    : timeFormatter.format(start);
};

const parseDate = (value?: string): Date | null => {
  if (!value) {
    return null;
  }

  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnlyMatch) {
    return new Date(
      Number(dateOnlyMatch[1]),
      Number(dateOnlyMatch[2]) - 1,
      Number(dateOnlyMatch[3])
    );
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
};

const parseMonth = (month: string, events: CalendarEvent[]): Date => {
  const explicit = parseDate(`${month}-01`) ?? parseDate(month);
  if (explicit) {
    return new Date(explicit.getFullYear(), explicit.getMonth(), 1);
  }

  const firstEvent = parseDate(events[0]?.start);
  if (firstEvent) {
    return new Date(firstEvent.getFullYear(), firstEvent.getMonth(), 1);
  }

  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), 1);
};

const inferYear = (events: CalendarEvent[]): number => {
  return parseDate(events[0]?.start)?.getFullYear() ?? new Date().getFullYear();
};

const buildMonthCells = (monthDate: Date): Array<Date | null> => {
  const start = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const end = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
  const blanks = Array.from({ length: start.getDay() }).map(() => null);
  const days = Array.from({ length: end.getDate() }).map(
    (_, index) => new Date(monthDate.getFullYear(), monthDate.getMonth(), index + 1)
  );

  return [...blanks, ...days];
};

const startOfWeek = (date: Date): Date => {
  const start = new Date(date);
  start.setDate(date.getDate() - date.getDay());
  start.setHours(0, 0, 0, 0);
  return start;
};

const addDays = (date: Date, days: number): Date => {
  const next = new Date(date);
  next.setDate(date.getDate() + days);
  return next;
};

const toDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const eventDateKey = (event: CalendarEvent): string => {
  if (/^\d{4}-\d{2}-\d{2}/.test(event.start)) {
    return event.start.slice(0, 10);
  }

  const parsed = parseDate(event.start);
  return parsed ? toDateKey(parsed) : event.start;
};

const eventsForDate = (events: CalendarEvent[], dateKey: string): CalendarEvent[] => {
  return events.filter(event => eventDateKey(event) === dateKey);
};

const eventsForMonth = (
  events: CalendarEvent[],
  year: number,
  monthIndex: number
): CalendarEvent[] => {
  const monthPrefix = `${year}-${String(monthIndex + 1).padStart(2, "0")}`;
  return events.filter(event => eventDateKey(event).startsWith(monthPrefix));
};

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const ReferenceList = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const ReferenceRowWrapper = styled.div`
  min-width: 0;

  .mds-item-list-row {
    padding-inline: ${({ theme }) => theme.spacing["2"]};
    padding-block: ${({ theme }) => theme.spacing["3"]};
    min-height: 52px;
    background: transparent;
  }
`;

const ReferenceTitle = styled.div`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.35;
`;

const ReferenceSecondary = styled.div`
  min-width: 0;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 13px;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TopAlignedAddon = styled.span`
  align-self: flex-start;
  display: inline-flex;
  margin-top: 1px;
  padding-right: ${({ theme }) => theme.spacing["3"]};
`;

const ReferenceRightAddon = styled.span`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-left: ${({ theme }) => theme.spacing["3"]};
  min-width: 0;
`;

const ResourceIconChip = styled.span`
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ theme }) => theme.colors.background.button.onChrome.hover};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const EventActions = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const LinkedSurface = styled(OptionalLink)`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing["3"]};
  min-width: 0;
  padding: ${({ theme }) => theme.spacing["3"]};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  color: inherit;
  text-decoration: none;
  background: ${({ theme }) => theme.colors.background.button.onChrome.default};
`;

const CollectionShortcutSurface = styled(OptionalLink)`
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr);
  align-items: start;
  column-gap: ${({ theme }) => theme.spacing["3"]};
  min-width: 0;
  padding: ${({ theme }) => theme.spacing["3"]} 0;
  color: inherit;
  text-decoration: none;
`;

const TimelineContent = styled(LinkedSurface)`
  display: flex;
  align-items: flex-start;
  flex: 1;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing["3"]};
`;

const TimelineMain = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const ShortcutIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  display: grid;
  place-items: center;
  background: ${({ theme }) => theme.colors.background.button.onChrome.hover};
  color: ${({ theme }) => theme.colors.text.secondary};
  flex: 0 0 auto;
`;

const ItemContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const ItemTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
  min-width: 0;
`;

const BodyText = styled.div`
  font-size: 13px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const MetaText = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

const EmptyState = styled.div`
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: 13px;
  padding: ${({ theme }) => theme.spacing["2"]} 0;
`;

const InlineSources = styled.div<{ $align: SourceChipAlignment }>`
  display: flex;
  align-items: center;
  align-content: flex-start;
  flex-wrap: wrap;
  align-self: ${({ $align }) => ($align === "end" ? "flex-start" : "stretch")};
  flex: 0 1 auto;
  justify-content: ${({ $align }) => ($align === "end" ? "flex-end" : "flex-start")};
  gap: 4px;
  margin-top: 2px;
  width: ${({ $align }) => ($align === "end" ? "auto" : "100%")};
  max-width: 100%;
  min-width: 0;

  > * {
    flex: 0 1 auto;
  }
`;

const ArticleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const ArticleBody = styled(BodyText)`
  max-height: 360px;
  overflow: auto;
  white-space: normal;
`;

const ArticleMarkdown = styled(MemCommonMarkdown)`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes["text-s"]};
  line-height: 1.5;
  user-select: text;
  text-align: start;
  word-spacing: normal;

  * {
    user-select: text;
  }

  && .markdown,
  && .markdown p,
  && .markdown li {
    text-align: start;
    word-spacing: normal;
  }

  && .markdown a.mem-chip {
    flex: 0 1 auto;
    max-width: 100%;
  }
`;

const TimelineList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["3"]};
  position: relative;
`;

const TimelineRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: ${({ theme }) => theme.spacing["3"]};
`;

const TimelineMarker = styled.div`
  width: 10px;
  margin-top: 16px;
  flex: 0 0 auto;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 10px;
    bottom: -32px;
    left: 4px;
    width: 1px;
    background: ${({ theme }) => theme.colors.border.default};
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.border.emphasis};
  }
`;

const DatePill = styled.div`
  min-width: 82px;
  padding: 4px 8px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.background.button.onChrome.hover};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 12px;
  text-align: center;
  flex: 0 0 auto;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${({ theme }) => theme.spacing["3"]};
`;

const GridCard = styled(OptionalLink)`
  min-width: 0;
  color: inherit;
  text-decoration: none;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.background.button.onChrome.default};
`;

const GridCardBody = styled(ItemContent)`
  padding: ${({ theme }) => theme.spacing["3"]};
`;

const CardSourcesButton = styled.button`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 4px;
  margin-top: ${({ theme }) => theme.spacing["1"]};
  padding: 2px 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.tertiary};
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  line-height: 1.3;

  &:hover {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const CardSourcesCount = styled.span`
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

const SourcesNotesDialog = styled(MDSModalDialog)`
  .mds-dialog {
    width: min(560px, 92vw);
  }
`;

const SourcesDialogBody = styled.div`
  min-width: 0;
`;

const CardImage = styled(ProxiedImage)`
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  display: block;
`;

const CardImageFallback = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  display: grid;
  place-items: center;
  background: ${({ theme }) => theme.colors.background.button.onChrome.hover};
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

const ImageFigure = styled(OptionalLink)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["2"]};
  color: inherit;
  text-decoration: none;
`;

const HeroImage = styled(ProxiedImage)`
  width: 100%;
  max-height: 460px;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  display: block;
`;

const ImagePlaceholder = styled.div`
  min-height: 180px;
  display: grid;
  place-items: center;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ theme }) => theme.colors.background.button.onChrome.hover};
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

const Caption = styled.div`
  font-size: 13px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: ${({ theme }) => theme.spacing["3"]};
`;

const ImageTile = styled(ImageFigure)`
  min-width: 0;
`;

const TileImage = styled(ProxiedImage)`
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  display: block;
`;

const NewNoteButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: 999px;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  padding: 5px 10px;
  flex: 0 0 auto;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.background.button.onChrome.hover};
  }

  &:disabled {
    cursor: not-allowed;
    color: ${({ theme }) => theme.colors.text.tertiary};
    opacity: 0.65;
  }
`;

const AgendaStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const YearGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: ${({ theme }) => theme.spacing["3"]};
`;

const MiniMonth = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: ${({ theme }) => theme.spacing["2"]};
  min-height: 86px;
`;

const MiniMonthTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing["2"]};
`;

const MiniMonthDays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 2px;
`;

const MiniMonthDayCell = styled.div`
  position: relative;
  min-width: 0;
  min-height: 18px;

  &:hover [data-mini-day-preview] {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }
`;

const MiniMonthDay = styled.div<{ $hasEvents: boolean }>`
  display: grid;
  place-items: center;
  height: 18px;
  border-radius: 50%;
  font-size: 11px;
  background: ${({ $hasEvents, theme }) =>
    $hasEvents ? theme.colors.background.banner.emphasis : "transparent"};
  color: ${({ $hasEvents, theme }) =>
    $hasEvents ? theme.colors.text.secondary : theme.colors.text.tertiary};
`;

const MiniDayPreview = styled.div`
  position: absolute;
  z-index: 3;
  left: 50%;
  bottom: calc(100% + 6px);
  display: none;
  width: max-content;
  max-width: 220px;
  transform: translateX(-50%);
  padding: ${({ theme }) => theme.spacing["2"]};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ theme }) => theme.colors.surface.popover};
  box-shadow: ${({ theme }) => theme.shadows.surface};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 12px;
  line-height: 1.35;
`;

const MiniDayPreviewItem = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MonthGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  overflow: hidden;
`;

const WeekdayHeader = styled.div`
  padding: 6px;
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.tertiary};
  background: ${({ theme }) => theme.colors.background.button.onChrome.default};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
`;

const MonthDay = styled.div<{ $isMuted: boolean }>`
  min-height: 84px;
  padding: 6px;
  border-right: 1px solid ${({ theme }) => theme.colors.border.default};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
  opacity: ${({ $isMuted }) => ($isMuted ? 0.35 : 1)};
  overflow: hidden;
`;

const DayNumber = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-bottom: 4px;
`;

const CalendarPill = styled.div`
  padding: 3px 6px;
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.background.banner.emphasis};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 11px;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const WeekGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(110px, 1fr));
  gap: ${({ theme }) => theme.spacing["2"]};
  overflow-x: auto;
`;

const WeekDay = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 160px;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: ${({ theme }) => theme.spacing["2"]};
`;

const WeekDayTitle = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

const EmptyMiniText = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.tertiary};
`;
